/**
 * MCP (Model Context Protocol) Type Definitions
 *
 * Types for MCP server configuration, tool definitions, and runtime state.
 * MCP enables external tool servers to provide capabilities to AI agents.
 *
 * Reference: https://modelcontextprotocol.io/
 */

import { z } from 'zod';

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Counter for generating unique IDs within a session
 */
let idCounter = 0;

/**
 * Generate a unique ID for MCP entities
 * Uses a combination of timestamp and counter to avoid collisions
 */
export function generateMCPId(prefix: string = 'mcp'): string {
  idCounter += 1;
  return `${prefix}-${Date.now()}-${idCounter}`;
}

/**
 * Reset ID counter (for testing purposes only)
 */
export function resetMCPIdCounter(): void {
  idCounter = 0;
}

// =============================================================================
// MCP SERVER CONFIGURATION
// =============================================================================

/**
 * Transport type for MCP server communication
 */
export type MCPTransportType = 'stdio' | 'sse' | 'http';

/**
 * MCP server connection status
 */
export type MCPServerStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Authentication type for MCP servers
 */
export type MCPAuthType = 'none' | 'api-key' | 'oauth' | 'managed-identity';

/**
 * MCP server authentication configuration
 */
export interface MCPServerAuth {
  /** Authentication type */
  type: MCPAuthType;
  /** Reference to connection/credential (not the actual secret) */
  credentialReference?: string;
  /** OAuth scopes if using oauth auth */
  scopes?: string[];
}

/**
 * MCP Server configuration
 * Defines how to connect to an MCP server
 */
export interface MCPServerConfig {
  /** Unique identifier for this server configuration */
  id: string;
  /** Display name for the server */
  name: string;
  /** Optional description */
  description?: string;
  /** Transport type for communication */
  transport: MCPTransportType;
  /** Server endpoint URL (for http/sse) or command (for stdio) */
  endpoint: string;
  /** Command arguments (for stdio transport) */
  args?: string[];
  /** Environment variables (for stdio transport) */
  env?: Record<string, string>;
  /** Authentication configuration */
  auth?: MCPServerAuth;
  /** Connection timeout in milliseconds */
  timeout?: number;
  /** Whether to auto-reconnect on disconnect */
  autoReconnect?: boolean;
  /** Icon URL for UI display */
  iconUri?: string;
  /** Brand color for UI display */
  brandColor?: string;
  /**
   * Optional MCP connection key (matches `connections.json.agentMcpConnections`
   * record key). Set by BYO server cards in the picker so the agent tool
   * can carry a connection reference that the runtime resolves via
   * `inputs.connectionReference.connectionName`.
   */
  connectionId?: string;
  /**
   * Managed connector id (e.g. `/managedApis/foundryamplitudemcp`). Set by
   * managed (Microsoft) server cards so the agent tool can create a
   * managed connection and resolve the swagger MCP path at config time.
   */
  connectorId?: string;
  /**
   * Swagger operation path for the managed MCP server's InvokeMCP operation,
   * used by the runtime's managed list-tools probe. Carried in the agent
   * tool's `inputs.parameters.mcpServerPath`.
   */
  mcpServerPath?: string;
  /**
   * Operation id of the managed MCP server's InvokeMCP operation (the
   * control-plane `name`). Transient — used at selection time to resolve
   * `mcpServerPath` from the connector swagger; never persisted to the tool.
   * Intentionally omitted from `MCPServerConfigSchema` so it can't survive a
   * round-trip through schema validation into persisted state.
   */
  operationId?: string;
}

/**
 * Reference to an MCP server from a tool definition
 */
export interface MCPServerReference {
  /** Server configuration ID */
  serverId: string;
  /** Server display name (for UI) */
  serverName: string;
  /** Optional connection reference for authentication */
  connectionId?: string;
}

// =============================================================================
// MCP TOOL DEFINITIONS
// =============================================================================

/**
 * Agent tool kinds - discriminator for tool types
 */
export type AgentToolKind = 'workflow' | 'function' | 'code' | 'builtin' | 'mcp';

/**
 * Base interface for all agent tools
 */
export interface AgentToolBase {
  /** Tool kind discriminator */
  kind: AgentToolKind;
  /** Tool name (identifier) */
  name: string;
  /** Human-readable description */
  description?: string;
  /** Whether the tool is enabled */
  enabled?: boolean;
}

/**
 * JSON Schema type for tool parameters
 */
export interface JSONSchema {
  type?: string;
  properties?: Record<string, JSONSchema>;
  required?: string[];
  items?: JSONSchema;
  description?: string;
  enum?: unknown[];
  default?: unknown;
  format?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  additionalProperties?: boolean | JSONSchema;
  [key: string]: unknown;
}

/**
 * MCP Tool Definition
 * Represents a tool provided by an MCP server
 */
export interface MCPToolDefinition extends AgentToolBase {
  kind: 'mcp';
  /** Reference to the MCP server providing this tool */
  mcpServer: MCPServerReference;
  /** Tool name as exposed by the MCP server */
  toolName: string;
  /** Input parameters schema (JSON Schema) */
  inputSchema?: JSONSchema;
  /** Output schema (if known) */
  outputSchema?: JSONSchema;
  /** Annotations/metadata from the MCP server */
  annotations?: Record<string, unknown>;
}

/**
 * Workflow-based tool definition (existing pattern)
 */
export interface WorkflowToolDefinition extends AgentToolBase {
  kind: 'workflow';
  /** Nested workflow actions */
  actions?: Record<string, unknown>;
  /** Agent parameter schema */
  agentParameterSchema?: Record<string, unknown>;
}

/**
 * Function-based tool definition (existing pattern)
 */
export interface FunctionToolDefinition extends AgentToolBase {
  kind: 'function';
  /** Operation ID for the function */
  operationId: string;
  /** Connector ID */
  connectorId?: string;
  /** Input schema */
  inputSchema?: Record<string, unknown>;
}

/**
 * Code interpreter tool definition
 */
export interface CodeToolDefinition extends AgentToolBase {
  kind: 'code';
  /** Programming language */
  language?: 'python' | 'javascript';
}

/**
 * Built-in tool definition
 */
export interface BuiltinToolDefinition extends AgentToolBase {
  kind: 'builtin';
  /** Built-in tool type */
  builtinType: string;
}

/**
 * Union of all tool definition types
 */
export type AgentToolDefinition =
  | MCPToolDefinition
  | WorkflowToolDefinition
  | FunctionToolDefinition
  | CodeToolDefinition
  | BuiltinToolDefinition;

// =============================================================================
// MCP RUNTIME STATE
// =============================================================================

/**
 * Runtime state for an MCP server connection
 */
export interface MCPServerState {
  /** Server configuration */
  config: MCPServerConfig;
  /** Current connection status */
  status: MCPServerStatus;
  /** Error message if status is 'error' */
  error?: string;
  /** Last successful connection time */
  lastConnected?: Date;
  /** Available tools from this server */
  availableTools: MCPToolInfo[];
  /** Available prompts from this server */
  availablePrompts?: MCPPromptInfo[];
  /** Available resources from this server */
  availableResources?: MCPResourceInfo[];
}

/**
 * Tool information from MCP server discovery
 */
export interface MCPToolInfo {
  /** Tool name */
  name: string;
  /** Tool description */
  description?: string;
  /** Input schema */
  inputSchema?: JSONSchema;
  /** Annotations */
  annotations?: Record<string, unknown>;
}

/**
 * Prompt information from MCP server discovery
 */
export interface MCPPromptInfo {
  /** Prompt name */
  name: string;
  /** Prompt description */
  description?: string;
  /** Arguments the prompt accepts */
  arguments?: Array<{
    name: string;
    description?: string;
    required?: boolean;
  }>;
}

/**
 * Resource information from MCP server discovery
 */
export interface MCPResourceInfo {
  /** Resource URI */
  uri: string;
  /** Resource name */
  name: string;
  /** Resource description */
  description?: string;
  /** MIME type */
  mimeType?: string;
}

// =============================================================================
// ZOD SCHEMAS FOR VALIDATION
// =============================================================================

/**
 * Zod schema for MCP transport type
 */
export const MCPTransportTypeSchema = z.enum(['stdio', 'sse', 'http']);

/**
 * Zod schema for MCP server status
 */
export const MCPServerStatusSchema = z.enum(['disconnected', 'connecting', 'connected', 'error']);

/**
 * Zod schema for MCP auth type
 */
export const MCPAuthTypeSchema = z.enum(['none', 'api-key', 'oauth', 'managed-identity']);

/**
 * Zod schema for MCP server authentication
 */
export const MCPServerAuthSchema = z.object({
  type: MCPAuthTypeSchema,
  credentialReference: z.string().optional(),
  scopes: z.array(z.string()).optional(),
});

/**
 * Zod schema for MCP server configuration
 */
export const MCPServerConfigSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  transport: MCPTransportTypeSchema,
  endpoint: z.string().min(1),
  args: z.array(z.string()).optional(),
  env: z.record(z.string()).optional(),
  auth: MCPServerAuthSchema.optional(),
  timeout: z.number().positive().optional(),
  autoReconnect: z.boolean().optional(),
  iconUri: z.string().url().optional(),
  brandColor: z.string().optional(),
  connectionId: z.string().optional(),
  connectorId: z.string().optional(),
  mcpServerPath: z.string().optional(),
});

/**
 * Zod schema for MCP server reference
 */
export const MCPServerReferenceSchema = z.object({
  serverId: z.string().min(1),
  serverName: z.string().min(1),
  connectionId: z.string().optional(),
});

/**
 * Zod schema for agent tool kind
 */
export const AgentToolKindSchema = z.enum(['workflow', 'function', 'code', 'builtin', 'mcp']);

/**
 * Zod schema for JSON Schema (simplified, recursive)
 * Note: We use a broader type annotation to avoid exactOptionalPropertyTypes issues
 */
export const JSONSchemaSchema: z.ZodType<Record<string, unknown>> = z.lazy(() =>
  z.object({
    type: z.string().optional(),
    properties: z.record(z.lazy(() => JSONSchemaSchema)).optional(),
    required: z.array(z.string()).optional(),
    items: z.lazy(() => JSONSchemaSchema).optional(),
    description: z.string().optional(),
    enum: z.array(z.unknown()).optional(),
    default: z.unknown().optional(),
    format: z.string().optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    pattern: z.string().optional(),
    additionalProperties: z.union([z.boolean(), z.lazy(() => JSONSchemaSchema)]).optional(),
  }).passthrough()
);

/**
 * Zod schema for MCP tool definition
 */
export const MCPToolDefinitionSchema = z.object({
  kind: z.literal('mcp'),
  name: z.string().min(1),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  mcpServer: MCPServerReferenceSchema,
  toolName: z.string().min(1),
  inputSchema: JSONSchemaSchema.optional(),
  outputSchema: JSONSchemaSchema.optional(),
  annotations: z.record(z.unknown()).optional(),
});

/**
 * Zod schema for workflow tool definition
 */
export const WorkflowToolDefinitionSchema = z.object({
  kind: z.literal('workflow'),
  name: z.string().min(1),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  actions: z.record(z.unknown()).optional(),
  agentParameterSchema: z.record(z.unknown()).optional(),
});

/**
 * Zod schema for function tool definition
 */
export const FunctionToolDefinitionSchema = z.object({
  kind: z.literal('function'),
  name: z.string().min(1),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  operationId: z.string().min(1),
  connectorId: z.string().optional(),
  inputSchema: z.record(z.unknown()).optional(),
});

/**
 * Zod schema for code tool definition
 */
export const CodeToolDefinitionSchema = z.object({
  kind: z.literal('code'),
  name: z.string().min(1),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  language: z.enum(['python', 'javascript']).optional(),
});

/**
 * Zod schema for builtin tool definition
 */
export const BuiltinToolDefinitionSchema = z.object({
  kind: z.literal('builtin'),
  name: z.string().min(1),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
  builtinType: z.string().min(1),
});

/**
 * Zod schema for any agent tool definition (discriminated union)
 */
export const AgentToolDefinitionSchema = z.discriminatedUnion('kind', [
  MCPToolDefinitionSchema,
  WorkflowToolDefinitionSchema,
  FunctionToolDefinitionSchema,
  CodeToolDefinitionSchema,
  BuiltinToolDefinitionSchema,
]);

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard for MCP tool definition
 */
export function isMCPTool(tool: AgentToolDefinition): tool is MCPToolDefinition {
  return tool.kind === 'mcp';
}

/**
 * Type guard for workflow tool definition
 */
export function isWorkflowTool(tool: AgentToolDefinition): tool is WorkflowToolDefinition {
  return tool.kind === 'workflow';
}

/**
 * Type guard for function tool definition
 */
export function isFunctionTool(tool: AgentToolDefinition): tool is FunctionToolDefinition {
  return tool.kind === 'function';
}

/**
 * Type guard for code tool definition
 */
export function isCodeTool(tool: AgentToolDefinition): tool is CodeToolDefinition {
  return tool.kind === 'code';
}

/**
 * Type guard for builtin tool definition
 */
export function isBuiltinTool(tool: AgentToolDefinition): tool is BuiltinToolDefinition {
  return tool.kind === 'builtin';
}

/**
 * Type guard for MCP server config validation
 */
export function isValidMCPServerConfig(config: unknown): config is MCPServerConfig {
  return MCPServerConfigSchema.safeParse(config).success;
}

/**
 * Type guard for MCP tool definition validation
 */
export function isValidMCPToolDefinition(tool: unknown): tool is MCPToolDefinition {
  return MCPToolDefinitionSchema.safeParse(tool).success;
}

// =============================================================================
// VALIDATION WITH ERROR DETAILS
// =============================================================================

/**
 * Validation result with error details
 */
export interface ValidationResult<T> {
  success: boolean;
  data?: T;
  errors?: Array<{
    path: string;
    message: string;
  }>;
}

/**
 * Validate MCP server config with detailed error messages
 */
export function validateMCPServerConfig(config: unknown): ValidationResult<MCPServerConfig> {
  const result = MCPServerConfigSchema.safeParse(config);
  if (result.success) {
    // Cast is safe because Zod validation ensures compatibility
    return { success: true, data: result.data as MCPServerConfig };
  }
  return {
    success: false,
    errors: result.error.issues.map((issue) => ({
      path: issue.path.join('.'),
      message: issue.message,
    })),
  };
}

/**
 * Validate MCP tool definition with detailed error messages
 */
export function validateMCPToolDefinition(tool: unknown): ValidationResult<MCPToolDefinition> {
  const result = MCPToolDefinitionSchema.safeParse(tool);
  if (result.success) {
    // Cast is safe because Zod validation ensures compatibility
    return { success: true, data: result.data as MCPToolDefinition };
  }
  return {
    success: false,
    errors: result.error.issues.map((issue) => ({
      path: issue.path.join('.'),
      message: issue.message,
    })),
  };
}

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Create a new MCP server configuration with defaults
 */
export function createMCPServerConfig(
  partial: Partial<MCPServerConfig> & Pick<MCPServerConfig, 'name' | 'endpoint'>
): MCPServerConfig {
  const config: MCPServerConfig = {
    id: partial.id ?? generateMCPId('mcp-server'),
    name: partial.name,
    endpoint: partial.endpoint,
    transport: partial.transport ?? 'http',
    auth: partial.auth ?? { type: 'none' },
    timeout: partial.timeout ?? 30000,
    autoReconnect: partial.autoReconnect ?? true,
    brandColor: partial.brandColor ?? '#6B7280',
  };

  // Only add optional properties if they have values (exactOptionalPropertyTypes)
  if (partial.description !== undefined) config.description = partial.description;
  if (partial.args !== undefined) config.args = partial.args;
  if (partial.env !== undefined) config.env = partial.env;
  if (partial.iconUri !== undefined) config.iconUri = partial.iconUri;

  return config;
}

/**
 * Create a new MCP tool definition from server tool info
 */
export function createMCPToolFromServerInfo(
  toolInfo: MCPToolInfo,
  serverRef: MCPServerReference
): MCPToolDefinition {
  const tool: MCPToolDefinition = {
    kind: 'mcp',
    name: toolInfo.name,
    enabled: true,
    mcpServer: serverRef,
    toolName: toolInfo.name,
  };

  // Only add optional properties if they have values (exactOptionalPropertyTypes)
  if (toolInfo.description !== undefined) tool.description = toolInfo.description;
  if (toolInfo.inputSchema !== undefined) tool.inputSchema = toolInfo.inputSchema;
  if (toolInfo.annotations !== undefined) tool.annotations = toolInfo.annotations;

  return tool;
}

/**
 * Create an empty MCP tool definition
 */
export function createEmptyMCPTool(serverRef: MCPServerReference): MCPToolDefinition {
  return {
    kind: 'mcp',
    name: '',
    description: '',
    enabled: true,
    mcpServer: serverRef,
    toolName: '',
  };
}
